import type { CouponsDataSource } from '~/api/data-source/coupons';
import { couponsDataSource } from '~/api/data-source/coupons';
import type {
  BuyerCoupon,
  BuyerCouponDetail,
  CheckoutCoupon,
  CouponDetail,
  DownloadedCoupon,
  PointCouponItem,
} from '~/api/model';
import {
  buyerCouponDetailSchema,
  buyerCouponSchema,
  checkoutCouponSchema,
  couponDetailSchema,
  downloadedCouponSchema,
  parseModel,
  parseModelArray,
  pointCouponItemSchema,
} from '~/api/model';
import type { CART_TYPE } from '~/hooks/useCartTypeParam';

export interface CouponsRepository {
  // Query
  getBuyerCoupons(params?: Record<string, unknown>): Promise<BuyerCoupon[]>;
  getBuyerCouponCount(): Promise<number>;
  getBuyerCouponDetail(buyerCouponId: number): Promise<BuyerCouponDetail>;
  getCheckoutCoupons(cartType: CART_TYPE): Promise<CheckoutCoupon>;
  getDownloadedCoupons(couponIds: number[]): Promise<DownloadedCoupon[]>;
  getPointCouponList(): Promise<PointCouponItem[]>;
  getCouponDetail(couponId: number): Promise<CouponDetail>;

  // Mutation
  downloadAllCoupons(couponIds: number[]): Promise<void>;
  downloadCoupon(couponId: number): Promise<void>;
  issuePointCoupon(couponId: number): Promise<void>;
}

export class CouponsRepositoryImpl implements CouponsRepository {
  // eslint-disable-next-line no-useless-constructor
  constructor(
    private readonly dataSource: CouponsDataSource = couponsDataSource,
  ) {}

  async getBuyerCoupons(params?: Record<string, unknown>) {
    const response = await this.dataSource.getBuyerCoupons(params);
    return parseModelArray(buyerCouponSchema, response, 'BuyerCoupon');
  }

  async getBuyerCouponCount() {
    return this.dataSource.getBuyerCouponCount();
  }

  async getBuyerCouponDetail(buyerCouponId: number) {
    const response = await this.dataSource.getBuyerCouponDetail(buyerCouponId);
    return parseModel(buyerCouponDetailSchema, response, 'BuyerCouponDetail');
  }

  async getCheckoutCoupons(cartType: CART_TYPE) {
    const response = await this.dataSource.getCheckoutCoupons(cartType);
    return parseModel(checkoutCouponSchema, response, 'CheckoutCoupon');
  }

  async getDownloadedCoupons(couponIds: number[]) {
    const response = await this.dataSource.getDownloadedCoupons(couponIds);
    const parsed = parseModelArray(
      downloadedCouponSchema,
      response,
      'DownloadedCoupon',
    );
    // 기존 로직 유지: 역순 정렬
    return [...parsed].reverse();
  }

  async getPointCouponList() {
    const response = await this.dataSource.getPointCouponList();
    return parseModelArray(pointCouponItemSchema, response, 'PointCouponItem');
  }

  async getCouponDetail(couponId: number) {
    const response = await this.dataSource.getCouponDetail(couponId);
    return parseModel(couponDetailSchema, response, 'CouponDetail');
  }

  async downloadAllCoupons(couponIds: number[]) {
    return this.dataSource.downloadAllCoupons(couponIds);
  }

  async downloadCoupon(couponId: number) {
    return this.dataSource.downloadCoupon(couponId);
  }

  async issuePointCoupon(couponId: number) {
    return this.dataSource.issuePointCoupon(couponId);
  }
}

export const couponsRepository = new CouponsRepositoryImpl();
